import type { AppLink } from "./buildAppLink";
import { buildOffersPageLink } from "./buildOffersPageLink";
import { DISCOVERY_DECADES } from "./discoveryDecades";

export interface CuratedCrate {
  id: string;
  startYear: number;
  endYear: number;
  taxonomy: { kind: "genre" | "style"; key: string };
  /** Every crate spans all formats (vinyl, CD, cassette…); its offers link carries no `format`. */
  format: "all";
  footer: boolean;
}

/** Minimum live offers for a crate to render; thinner crates stay hidden. */
export const CURATED_CRATE_MIN_OFFERS = 20;

type CrateSeed = readonly [
  startYear: number,
  kind: "genre" | "style",
  key: string,
  footer?: "footer",
];

// Editorial order: decade-defining genres and styles chosen by music history and collector
// demand, 2026-09. Footer crates (see curatedCrates.test.ts) are seeded first, in the order the
// footer renders them; the remaining crates follow in decade order, each decade in its own
// editorial order.
const CRATE_SEEDS: readonly CrateSeed[] = [
  // Footer picks, in footer order.
  [1970, "style", "classic_rock", "footer"],
  [1980, "style", "heavy_metal", "footer"],
  [1980, "style", "synth_pop", "footer"],
  [1990, "style", "grunge", "footer"],
  [1990, "genre", "hip_hop", "footer"],
  [1960, "style", "bossa_nova", "footer"],
  [1970, "style", "mpb", "footer"],
  [1970, "style", "disco", "footer"],

  // 1950s.
  [1950, "style", "rock_and_roll"],
  [1950, "style", "rockabilly"],
  [1950, "style", "doo_wop"],
  [1950, "style", "cool_jazz"],
  [1950, "style", "hard_bop"],

  // 1960s (bossa_nova is a footer pick above).
  [1960, "style", "mpb"],
  [1960, "style", "psychedelic_rock"],
  [1960, "style", "soul"],
  [1960, "style", "garage_rock"],
  [1960, "style", "surf"],
  [1960, "genre", "jazz"],

  // 1970s (classic_rock, mpb and disco are footer picks above).
  [1970, "style", "prog_rock"],
  [1970, "style", "punk"],
  [1970, "style", "funk"],
  [1970, "style", "roots_reggae"],
  [1970, "style", "samba"],
  [1970, "style", "samba_soul"],

  // 1980s (synth_pop and heavy_metal are footer picks above).
  [1980, "style", "new_wave"],
  [1980, "style", "post_punk"],
  [1980, "style", "thrash_metal"],
  [1980, "style", "hard_rock"],
  [1980, "genre", "hip_hop"],

  // 1990s (grunge and hip_hop are footer picks above).
  [1990, "style", "britpop"],
  [1990, "style", "alternative_rock"],
  [1990, "style", "trip_hop"],
  [1990, "style", "house"],

  // 2000s.
  [2000, "style", "indie_rock"],
  [2000, "style", "emo"],
  [2000, "style", "pop_punk"],
  [2000, "style", "nu_metal"],
  [2000, "style", "neo_soul"],

  // 2010s.
  [2010, "genre", "pop"],
  [2010, "style", "k_pop"],
  [2010, "style", "trap"],
  [2010, "style", "indie_pop"],
  [2010, "style", "synthwave"],

  // 2020s.
  [2020, "genre", "pop"],
  [2020, "style", "k_pop"],
  [2020, "genre", "hip_hop"],
];

function crateFromSeed([startYear, kind, key, footer]: CrateSeed): CuratedCrate {
  const decade = DISCOVERY_DECADES.find((candidate) => candidate.startYear === startYear);
  if (!decade) throw new Error(`Curated crate decade ${startYear} is not a discovery decade`);
  return {
    id: `${startYear}-${key}`,
    startYear,
    endYear: decade.endYear,
    taxonomy: { kind, key },
    format: "all",
    footer: footer === "footer",
  };
}

/** Curated decade × style/genre crates over every format: Garimpos hub, footer picks, sitemap. */
export const CURATED_CRATES: readonly CuratedCrate[] = CRATE_SEEDS.map(crateFromSeed);

/**
 * Search of the crate's canonical offers page: no format (every format is the page default) and no
 * sort. Keys follow the offers canonical order (genre/style, year) so the serialized link equals
 * the canonical URL.
 */
export function curatedCrateOffersSearch(crate: CuratedCrate): {
  genre?: [string];
  style?: [string];
  year: [number, number];
} {
  if (crate.taxonomy.kind === "genre") {
    return {
      genre: [crate.taxonomy.key],
      year: [crate.startYear, crate.endYear],
    };
  }
  return {
    style: [crate.taxonomy.key],
    year: [crate.startYear, crate.endYear],
  };
}

/** Absolute canonical offers-page URL for the crate. */
export function curatedCrateOffersLink(crate: CuratedCrate): AppLink {
  return buildOffersPageLink(
    crate.taxonomy.kind === "genre"
      ? {
          format: crate.format,
          yearFrom: crate.startYear,
          yearTo: crate.endYear,
          genre: [crate.taxonomy.key],
        }
      : {
          format: crate.format,
          yearFrom: crate.startYear,
          yearTo: crate.endYear,
          style: [crate.taxonomy.key],
        },
  );
}
